import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  Check,
  ChevronDown,
  Pencil,
  Plus,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import {
  computeCellStatusVisual,
  computeValidationCellVisual,
  rowVariantWrapperClass,
} from '../lib/cellStatus';
import {
  patchField,
  selectValueForOptions,
  summaryText,
} from '../lib/rowFieldHelpers';
import type { RowDiffKind } from '../lib/rowDiff';
import type {
  EditableColumnDef,
  EditableRow,
  EditableRowVariant,
  ValidateCellFn,
} from '../types';
import { CollapsedSummaryText } from './CollapsedSummaryText';
import { InlineStatusChip } from './InlineStatusChip';

export interface EditableRowTableRowProps {
  columns: EditableColumnDef[];
  /** Matches header row so action column lines up with the + control. */
  rowGridStyle?: CSSProperties;
  row: EditableRow;
  /** Required when editing (expanded draft or expanded committed, or inline draft/deleted). */
  onChange?: (next: EditableRow) => void;
  readOnly: boolean;
  /** Support role: row stays viewable/expandable (readOnly=false), but its
   *  commit actions (Save, Remove, Restore, Revert) are disabled. */
  commitDisabled?: boolean;
  variant: EditableRowVariant;
  onAdd?: () => void;
  onRemove?: () => void;
  /** When set (row differs from baseline), replaces delete with restore-saved-values. */
  onRevertToBaseline?: () => void;
  onRestore?: () => void;
  rowDiff?: RowDiffKind;
  modifiedFieldIds?: string[];
  validateCell?: ValidateCellFn;
  /** When false, per-cell validation UI is hidden (e.g. empty add row). */
  showValidationErrors?: boolean;
  /** When true, draft add-row (+) is disabled (e.g. no content). */
  addDisabled?: boolean;
  /** Committed rows: collapsed summary vs expanded labeled form. */
  committedLayout?: 'collapsed' | 'expanded';
  /** Collapsed row: enter edit mode. */
  onBeginEdit?: () => void;
  /** Expanded committed: commit buffer to parent. */
  onSaveEdit?: () => void;
  /** Expanded committed: discard buffer and collapse. */
  onCancelEdit?: () => void;
  /** After failed save, show validation on all expanded fields. */
  expandedShowValidation?: boolean;
  /** When another row is in edit mode, dims this row (collapsed rows + draft). */
  dimmed?: boolean;
}

export function EditableRowTableRow({
  columns,
  rowGridStyle,
  row,
  onChange,
  readOnly,
  commitDisabled = false,
  variant,
  onAdd,
  onRemove,
  onRevertToBaseline,
  onRestore,
  rowDiff,
  modifiedFieldIds = [],
  validateCell,
  showValidationErrors = true,
  addDisabled = false,
  committedLayout = 'collapsed',
  onBeginEdit,
  onSaveEdit,
  onCancelEdit,
  expandedShowValidation = false,
  dimmed = false,
}: EditableRowTableRowProps): ReactElement {
  const confirmContainerRef = useRef<HTMLDivElement | null>(null);
  const [confirmRemoveArmed, setConfirmRemoveArmed] = useState(false);
  const effectiveRowDiff = rowDiff ?? 'unchanged';
  const disabled = readOnly || variant === 'deleted' || commitDisabled;
  const modifiedSet = new Set(modifiedFieldIds);
  const isDeleted = variant === 'deleted';

  useEffect(() => {
    if (!confirmRemoveArmed) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setConfirmRemoveArmed(false);
    }, 4000);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [confirmRemoveArmed]);

  useEffect(() => {
    if (!confirmRemoveArmed) {
      return;
    }
    const onPointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (confirmContainerRef.current?.contains(target)) {
        return;
      }
      setConfirmRemoveArmed(false);
    };
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
    };
  }, [confirmRemoveArmed]);

  const handleRemoveClick = (): void => {
    if (confirmRemoveArmed) {
      return;
    }
    setConfirmRemoveArmed(true);
  };

  const handleConfirmRemove = (): void => {
    onRemove?.();
    setConfirmRemoveArmed(false);
  };

  const handleCancelRemove = (): void => {
    setConfirmRemoveArmed(false);
  };

  useEffect(() => {
    if (!confirmRemoveArmed) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setConfirmRemoveArmed(false);
        return;
      }
      if (event.key !== 'Enter') {
        return;
      }
      const el = event.target;
      if (!(el instanceof Element)) {
        return;
      }
      if (
        el.closest(
          'input, textarea, select, [contenteditable="true"], [data-radix-select-content]'
        )
      ) {
        return;
      }
      if (el.closest('[data-confirm-delete-popover]')) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      onRemove?.();
      setConfirmRemoveArmed(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [confirmRemoveArmed, onRemove]);

  const confirmDeletePopover = (
    <div
      data-confirm-delete-popover=""
      className="absolute right-9 top-1/2 z-20 flex -translate-y-1/2 items-center gap-2 rounded-md border border-border bg-popover px-2 py-1.5 text-popover-foreground shadow-md"
      onClick={e => e.stopPropagation()}
      role="dialog"
      aria-label="Confirm delete"
    >
      <span className="text-xs font-medium leading-none text-foreground">
        Confirm delete?
      </span>
      <div className="flex items-center gap-0.5">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-emerald-600 hover:text-emerald-700"
          onClick={handleConfirmRemove}
          aria-label="Confirm remove row"
        >
          <Check className="size-3.5" strokeWidth={2.25} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-muted-foreground"
          onClick={handleCancelRemove}
          aria-label="Cancel remove row"
        >
          <X className="size-3.5" strokeWidth={2.25} />
        </Button>
      </div>
    </div>
  );

  if (variant === 'committed' && committedLayout === 'collapsed') {
    return (
      <div
        style={rowGridStyle}
        className={cn(
          'grid gap-x-2 gap-y-1 items-center w-full min-w-0 border-b border-border/40 dark:border-border/80 py-2 pl-0.5 pr-1',
          rowVariantWrapperClass(variant, effectiveRowDiff),
          dimmed && 'opacity-[0.38] transition-opacity duration-200',
          !readOnly &&
            cn(
              'group cursor-pointer transition-colors duration-150',
              effectiveRowDiff === 'new'
                ? 'hover:bg-emerald-500/25 dark:hover:bg-emerald-500/30'
                : 'hover:bg-muted/85',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
            )
        )}
        role={readOnly ? undefined : 'button'}
        tabIndex={readOnly ? undefined : 0}
        onClick={() => {
          if (!readOnly) {
            onBeginEdit?.();
          }
        }}
        onKeyDown={e => {
          if (readOnly) {
            return;
          }
          if (confirmRemoveArmed && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onBeginEdit?.();
          }
        }}
      >
        {columns.map(col => {
          const msg =
            validateCell && showValidationErrors
              ? validateCell({
                  row,
                  column: col,
                  value: row.fields[col.id] ?? '',
                })
              : undefined;
          const visual = msg
            ? computeValidationCellVisual(msg)
            : computeCellStatusVisual(
                col.id,
                variant,
                effectiveRowDiff,
                modifiedSet
              );
          const text = summaryText(col, row);
          const rawTrim = (row.fields[col.id] ?? '').trim();
          const isWildcardMuted =
            col.kind === 'text' && (rawTrim === '' || rawTrim === '*');
          return (
            <div
              key={col.id}
              className={cn(
                'flex min-w-0 items-center gap-1.5 text-sm',
                msg && 'rounded-sm bg-destructive/5 px-1.5 py-0.5',
                !msg &&
                  visual &&
                  effectiveRowDiff !== 'new' &&
                  'rounded-sm bg-muted/25 px-1.5 py-0.5',
                !msg && visual && effectiveRowDiff === 'new' && 'px-1.5 py-0.5'
              )}
            >
              {text.length === 0 ? (
                <span
                  className={cn(
                    'min-w-0 flex-1 truncate',
                    msg && 'text-destructive'
                  )}
                />
              ) : (
                <CollapsedSummaryText
                  text={text}
                  className={cn(
                    isWildcardMuted &&
                      (effectiveRowDiff === 'new'
                        ? 'text-emerald-900/75 dark:text-emerald-200/85'
                        : 'text-muted-foreground'),
                    msg && 'text-destructive'
                  )}
                />
              )}
              {visual ? (
                <span
                  title={msg ?? visual.label}
                  className={cn(
                    'shrink-0 rounded border px-0.5 py-px text-[8px] font-medium leading-none',
                    visual.chip
                  )}
                >
                  {visual.label}
                </span>
              ) : null}
            </div>
          );
        })}
        <div
          className={cn(
            'flex min-w-0 items-center justify-end gap-0.5 pr-0.5',
            effectiveRowDiff === 'new'
              ? 'text-emerald-950 dark:text-emerald-50'
              : ''
          )}
        >
          {onRevertToBaseline != null ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                'h-8 w-8',
                effectiveRowDiff === 'new'
                  ? 'text-emerald-900 hover:bg-emerald-500/30 dark:text-emerald-100 dark:hover:bg-emerald-500/25'
                  : 'text-muted-foreground'
              )}
              disabled={readOnly || commitDisabled}
              onClick={e => {
                e.stopPropagation();
                onRevertToBaseline();
              }}
              aria-label="Restore saved values"
            >
              <Undo2 className="size-3.5" />
            </Button>
          ) : (
            <div ref={confirmContainerRef} className="relative">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  'h-8 w-8',
                  effectiveRowDiff === 'new'
                    ? 'text-emerald-900 hover:bg-emerald-500/30 dark:text-emerald-100 dark:hover:bg-emerald-500/25'
                    : 'text-muted-foreground'
                )}
                disabled={disabled}
                onClick={e => {
                  e.stopPropagation();
                  handleRemoveClick();
                }}
                aria-label="Remove row"
              >
                <Trash2 className="size-3.5" />
              </Button>
              {confirmRemoveArmed && !disabled ? confirmDeletePopover : null}
            </div>
          )}
          {!readOnly ? (
            <div
              className="relative flex h-8 w-8 shrink-0 items-center justify-center"
              aria-hidden
            >
              <ChevronDown
                className={cn(
                  'absolute size-3.5 transition-opacity duration-150',
                  effectiveRowDiff === 'new'
                    ? 'text-emerald-900/80 dark:text-emerald-200/90'
                    : 'text-muted-foreground',
                  !dimmed && 'group-hover:opacity-0'
                )}
              />
              <Pencil
                className={cn(
                  'absolute size-3.5 transition-opacity duration-150',
                  effectiveRowDiff === 'new'
                    ? 'text-emerald-900 dark:text-emerald-100'
                    : 'text-muted-foreground',
                  dimmed ? 'hidden' : 'opacity-0 group-hover:opacity-100'
                )}
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (variant === 'committed' && committedLayout === 'expanded') {
    const showVal = showValidationErrors || expandedShowValidation;
    const disabledExpanded = readOnly;
    let expandedHasValidationErrors = false;
    if (validateCell && showVal) {
      for (const col of columns) {
        const msg = validateCell({
          row,
          column: col,
          value: row.fields[col.id] ?? '',
        });
        if (msg) {
          expandedHasValidationErrors = true;
          break;
        }
      }
    }

    return (
      <div
        className="w-full min-w-0 mt-1.5 rounded-md border border-border bg-background px-2 py-2.5"
        role="group"
        aria-label="Editing row"
      >
        <div className="flex flex-wrap items-end gap-x-3 gap-y-3">
          {columns.map(col => {
            const fieldId = `edit-${row.id}-${col.id}`;
            const errId = `${fieldId}-err`;
            const hintPlaceholder = col.placeholder;
            const rawValue = row.fields[col.id] ?? '';
            const validationMessage =
              validateCell && showVal
                ? validateCell({ row, column: col, value: rawValue })
                : undefined;
            const errorVisual = validationMessage
              ? computeValidationCellVisual(validationMessage)
              : null;
            const visual = validationMessage
              ? null
              : computeCellStatusVisual(
                  col.id,
                  variant,
                  effectiveRowDiff,
                  modifiedSet
                );
            const isInvalid = Boolean(validationMessage);

            return (
              <div
                key={col.id}
                className="flex min-h-0 min-w-[100px] flex-1 basis-[min(100%,12rem)] flex-col gap-1.5"
              >
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <Label
                    htmlFor={fieldId}
                    className="text-xs font-medium text-muted-foreground"
                  >
                    {col.headerLabel}
                  </Label>
                  {visual ? (
                    <span
                      className={cn(
                        'shrink-0 rounded border px-1 py-px text-[9px] font-medium leading-none',
                        visual.chip
                      )}
                    >
                      {visual.label}
                    </span>
                  ) : null}
                </div>
                {errorVisual ? (
                  <span id={errId} className="sr-only">
                    {validationMessage}
                  </span>
                ) : null}
                {col.kind === 'select' && col.options ? (
                  errorVisual ? (
                    <div
                      className={cn(
                        'relative w-full min-w-0 rounded-md border bg-background',
                        errorVisual.border,
                        'focus-within:ring-2 focus-within:ring-destructive/35 focus-within:ring-offset-0'
                      )}
                      role="alert"
                    >
                      <Select
                        value={selectValueForOptions(
                          row.fields[col.id],
                          col.options
                        )}
                        onValueChange={v =>
                          onChange?.(patchField(row, col.id, v))
                        }
                        disabled={disabledExpanded}
                      >
                        <SelectTrigger
                          id={fieldId}
                          size="sm"
                          className={cn(
                            'h-9 w-full border-0 bg-transparent pr-16 text-sm shadow-none',
                            'focus-visible:ring-0 focus-visible:ring-offset-0'
                          )}
                          aria-invalid={isInvalid}
                          aria-describedby={isInvalid ? errId : undefined}
                        >
                          <SelectValue placeholder={hintPlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {col.options.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <InlineStatusChip
                        label={errorVisual.label}
                        className={errorVisual.chip}
                        variant="select"
                        title={validationMessage}
                      />
                    </div>
                  ) : (
                    <div className="min-w-0 flex-1">
                      <Select
                        value={selectValueForOptions(
                          row.fields[col.id],
                          col.options
                        )}
                        onValueChange={v =>
                          onChange?.(patchField(row, col.id, v))
                        }
                        disabled={disabledExpanded}
                      >
                        <SelectTrigger
                          id={fieldId}
                          size="sm"
                          className="h-9 w-full text-sm"
                        >
                          <SelectValue placeholder={hintPlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {col.options.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )
                ) : errorVisual ? (
                  <div
                    className={cn(
                      'relative w-full min-w-0 rounded-md border bg-background',
                      errorVisual.border,
                      'focus-within:ring-2 focus-within:ring-destructive/35 focus-within:ring-offset-0'
                    )}
                    role="alert"
                  >
                    <Input
                      id={fieldId}
                      value={row.fields[col.id] ?? ''}
                      onChange={e =>
                        onChange?.(patchField(row, col.id, e.target.value))
                      }
                      placeholder={hintPlaceholder}
                      disabled={disabledExpanded}
                      className={cn(
                        'h-9 border-0 bg-transparent pr-14 text-sm shadow-none',
                        'focus-visible:ring-0 focus-visible:ring-offset-0'
                      )}
                      aria-invalid={isInvalid}
                      aria-describedby={isInvalid ? errId : undefined}
                    />
                    <InlineStatusChip
                      label={errorVisual.label}
                      className={errorVisual.chip}
                      variant="input"
                      title={validationMessage}
                    />
                  </div>
                ) : (
                  <Input
                    id={fieldId}
                    value={row.fields[col.id] ?? ''}
                    onChange={e =>
                      onChange?.(patchField(row, col.id, e.target.value))
                    }
                    placeholder={hintPlaceholder}
                    disabled={disabledExpanded}
                    className="h-9 min-w-0 text-sm"
                  />
                )}
              </div>
            );
          })}
        </div>
        {!readOnly && onSaveEdit && onCancelEdit ? (
          <div className="mt-3 flex justify-end gap-2 border-t border-border pt-2.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCancelEdit}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={onSaveEdit}
              disabled={expandedHasValidationErrors || commitDisabled}
            >
              Save
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={rowGridStyle}
      className={cn(
        'grid w-full min-w-0 gap-x-2 gap-y-1 items-end',
        rowVariantWrapperClass(variant, effectiveRowDiff),
        dimmed && 'opacity-[0.38] transition-opacity duration-200'
      )}
      role="row"
    >
      {columns.map(col => {
        const hintPlaceholder =
          variant === 'draft' ? col.placeholder : undefined;
        const rawValue = row.fields[col.id] ?? '';
        const validationMessage =
          !isDeleted && validateCell && showValidationErrors
            ? validateCell({ row, column: col, value: rawValue })
            : undefined;
        const visual = validationMessage
          ? computeValidationCellVisual(validationMessage)
          : computeCellStatusVisual(
              col.id,
              variant,
              effectiveRowDiff,
              modifiedSet
            );
        const isInvalid = Boolean(validationMessage);
        return (
          <div
            key={col.id}
            className={cn(
              'min-h-0 min-w-0',
              variant === 'draft' && 'flex flex-col justify-end gap-1'
            )}
          >
            {variant === 'draft' ? (
              <Label className="px-0.5 text-[10px] font-medium leading-none text-muted-foreground">
                {col.headerLabel}
              </Label>
            ) : null}
            {col.kind === 'select' && col.options ? (
              <div
                className={cn(
                  'relative w-full rounded-md',
                  visual
                    ? cn(
                        'border bg-background',
                        visual.border,
                        'focus-within:ring-2 focus-within:ring-ring/45 focus-within:ring-offset-0',
                        isInvalid &&
                          'focus-within:ring-destructive/35 focus-within:ring-2'
                      )
                    : 'border border-transparent focus-within:ring-2 focus-within:ring-ring/45 focus-within:ring-offset-0'
                )}
                role={isInvalid ? 'alert' : undefined}
              >
                <Select
                  value={selectValueForOptions(row.fields[col.id], col.options)}
                  onValueChange={v => onChange?.(patchField(row, col.id, v))}
                  disabled={disabled}
                >
                  <SelectTrigger
                    size="sm"
                    className={cn(
                      'h-8 w-full text-sm shadow-none',
                      visual
                        ? cn(
                            'border-0 bg-transparent pr-16',
                            'focus-visible:ring-0 focus-visible:ring-offset-0'
                          )
                        : isDeleted &&
                            'border-transparent bg-transparent opacity-90'
                    )}
                    aria-invalid={isInvalid}
                    aria-label={col.headerLabel}
                  >
                    <SelectValue placeholder={hintPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {col.options.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {visual ? (
                  <InlineStatusChip
                    label={visual.label}
                    className={visual.chip}
                    variant="select"
                    title={isInvalid ? validationMessage : undefined}
                  />
                ) : null}
              </div>
            ) : (
              <div
                className={cn(
                  'relative w-full rounded-md',
                  visual
                    ? cn(
                        'border bg-background',
                        visual.border,
                        'focus-within:ring-2 focus-within:ring-ring/45 focus-within:ring-offset-0',
                        isInvalid &&
                          'focus-within:ring-destructive/35 focus-within:ring-2'
                      )
                    : 'border border-transparent focus-within:ring-2 focus-within:ring-ring/45 focus-within:ring-offset-0'
                )}
                role={isInvalid ? 'alert' : undefined}
              >
                <Input
                  value={row.fields[col.id] ?? ''}
                  onChange={e =>
                    onChange?.(patchField(row, col.id, e.target.value))
                  }
                  onKeyDown={
                    variant === 'draft'
                      ? (e: ReactKeyboardEvent<HTMLInputElement>) => {
                          if (e.key !== 'Enter' || addDisabled) {
                            return;
                          }
                          e.preventDefault();
                          onAdd?.();
                        }
                      : undefined
                  }
                  placeholder={hintPlaceholder}
                  disabled={disabled}
                  className={cn(
                    'h-8 text-sm',
                    visual
                      ? cn(
                          'border-0 bg-transparent pr-14 shadow-none',
                          'focus-visible:ring-0 focus-visible:ring-offset-0',
                          isDeleted && 'line-through opacity-90'
                        )
                      : isDeleted &&
                          'border-transparent bg-transparent line-through'
                  )}
                  aria-invalid={isInvalid}
                  aria-label={col.headerLabel}
                />
                {visual ? (
                  <InlineStatusChip
                    label={visual.label}
                    className={visual.chip}
                    variant="input"
                    title={isInvalid ? validationMessage : undefined}
                  />
                ) : null}
              </div>
            )}
          </div>
        );
      })}
      <div className="flex h-8 w-full shrink-0 items-center justify-center self-end">
        {variant === 'draft' ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={disabled || addDisabled}
            onClick={() => onAdd?.()}
            aria-label="Add row"
          >
            <Plus className="size-3.5" />
          </Button>
        ) : variant === 'deleted' ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            disabled={readOnly}
            onClick={() => onRestore?.()}
            aria-label="Restore row"
          >
            <Undo2 className="size-3.5" />
          </Button>
        ) : onRevertToBaseline != null ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            disabled={readOnly}
            onClick={() => onRevertToBaseline()}
            aria-label="Restore saved values"
          >
            <Undo2 className="size-3.5" />
          </Button>
        ) : (
          <div ref={confirmContainerRef} className="relative">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              disabled={disabled}
              onClick={handleRemoveClick}
              aria-label="Remove row"
            >
              <Trash2 className="size-3.5" />
            </Button>
            {confirmRemoveArmed && !disabled ? confirmDeletePopover : null}
          </div>
        )}
      </div>
    </div>
  );
}
